import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const HeroSection = ({ userLocation }) => {
  const [showProductGallery, setShowProductGallery] = useState(false);
  const navigate = useNavigate();

  // Static hero background - always the same
  const heroBackground = "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1920&h=1080&fit=crop";

  const productGallery = [
    {
      id: 1,
      name: "Premium Coffee Beans",
      image: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&h=300&fit=crop",
      origin: "Ethiopian Highlands",
      certification: "Organic, Fair Trade"
    },
    {
      id: 2,
      name: "Artisan Textiles",
      image: "https://images.pexels.com/photos/6069112/pexels-photo-6069112.jpeg?w=400&h=300&fit=crop",
      origin: "West African Crafts",
      certification: "Handwoven, Authentic"
    },
    {
      id: 3,
      name: "Natural Minerals",
      image: "https://images.pixabay.com/photo/2016/04/14/18/29/gold-1329217_1280.jpg?w=400&h=300&fit=crop",
      origin: "South African Mines",
      certification: "Conflict-free, Certified"
    },
    {
      id: 4,
      name: "Exotic Spices",
      image: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&h=300&fit=crop",
      origin: "North African Markets",
      certification: "Pure, Grade A"
    }
  ];



  const navigateToConsultation = () => {
    navigate('/contact');
  };

  const navigateToProducts = () => {
    navigate('/products');
  };

  return (
    <section className="hero-section">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/assets/images/Nigerian Farm Video.mp4" type="video/mp4" />
          {/* Fallback image if video fails to load */}
          <Image
            src={heroBackground}
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-accent/80 to-secondary/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center pt-24 sm:pt-32 pb-16 sm:pb-20 lg:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-12"
        >
          {/* Main Headlines */}
          <motion.h2
            className="text-5xl sm:text-6xl lg:text-7xl text-white mb-8 font-montserrat font-bold leading-tight"
          >
            <div>Connecting Global Markets</div>
            <div>with Africa's Finest</div>
            <div className="text-secondary font-bold">
              Agricultural Excellence
            </div>
          </motion.h2>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
          className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 lg:space-x-8 mb-12 sm:mb-16"
        >
          <motion.button
            onClick={navigateToConsultation}
            className="w-full sm:w-auto bg-secondary hover:bg-secondary-700 text-charcoal px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-semibold flex items-center justify-center space-x-2 sm:space-x-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 font-montserrat"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Icon name="Calendar" size={20} className="sm:w-6 sm:h-6" />
            <span className="hidden sm:inline">Schedule Export Consultation</span>
            <span className="sm:hidden">Get Consultation</span>
          </motion.button>
          
          <motion.button
            onClick={navigateToProducts}
            className="w-full sm:w-auto bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-semibold flex items-center justify-center space-x-2 sm:space-x-3 rounded-lg transition-all duration-300 font-montserrat"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Icon name="Eye" size={20} className="sm:w-6 sm:h-6" />
            <span className="hidden sm:inline">View Product Catalog</span>
            <span className="sm:hidden">View Products</span>
          </motion.button>
        </motion.div>

        {/* Company Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: "easeOut" }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto"
        >
          {[
            { number: "5", label: "Product Categories" },
            { number: "Premium", label: "Quality Standards" },
            { number: "Global", label: "Market Reach" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 sm:p-8 text-center rounded-xl shadow-lg"
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="text-3xl sm:text-4xl font-bold text-white mb-2 sm:mb-3 font-montserrat">{stat.number}</div>
              <div className="text-sm sm:text-base text-white/80 font-montserrat font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>



      </div>

      {/* Floating Elements */}
      <motion.div
        className="absolute top-1/4 left-8 w-4 h-4 bg-accent/30 rounded-full animate-float"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 right-12 w-6 h-6 bg-primary/20 rounded-full animate-float"
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div
        className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-white/20 rounded-full animate-float"
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />

      {/* Product Gallery Modal */}
      {showProductGallery && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-secondary-900/90 backdrop-blur-xl z-50 flex items-center justify-center p-6"
          onClick={() => setShowProductGallery(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="glass-card max-w-6xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
                              <h3 className="heading-md text-white">Product Catalog</h3>
              <motion.button
                onClick={() => setShowProductGallery(false)}
                className="glass-button p-3 text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Icon name="X" size={24} />
              </motion.button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {productGallery.map((product, index) => (
                <motion.div
                  key={product.id}
                  className="glass-card p-4 sm:p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Image
                    src={product.image}
                    alt={product.name}
                    className="w-full h-40 sm:h-48 object-cover rounded-xl mb-3 sm:mb-4"
                  />
                                          <h4 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">{product.name}</h4>
                                      <p className="text-white/70 text-xs sm:text-sm mb-2">{product.origin}</p>
                  <span className="inline-block glass-button text-white px-2 sm:px-3 py-1 text-xs font-medium">
                    {product.certification}
                  </span>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-8 text-center">
              <motion.button
                onClick={navigateToConsultation}
                className="glass-button text-white px-8 py-3 font-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Product Samples
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default HeroSection;